import React, { useState } from 'react';
import { Button, Card, TableHead, IconButton, Paper, Snackbar, Table, TableCell, TableRow, Tooltip, Typography, TableBody, Stack, Avatar, Box } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Assignment, AudioFile, Cancel, FileDownload, Image, PictureAsPdf, TextSnippet, UploadFile, VideoFile } from '@mui/icons-material';

interface AttachmentUploadProps {
    onUploadSuccess?: (files: File[]) => void;
}

interface UploadedFile extends File {
    uploadedBy: string;
    uploadedAt: string;
    downloadCount: number;
    content: File;
}

const AttachmentUpload: React.FC<AttachmentUploadProps> = ({ onUploadSuccess }) => {
    const [selectedFiles, setSelectedFiles] = useState<UploadedFile[]>([]);
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>('');

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const filesArray = Array.from(files).map((file) => {
                const uploadedFile: UploadedFile = {
                    ...file,
                    uploadedBy: "user.id",
                    uploadedAt: new Date().toISOString(),
                    downloadCount: 0,
                    content: file,
                };
                return uploadedFile;
            });

            setSelectedFiles((prevFiles) => [...prevFiles, ...filesArray]);
        }
    };

    // Handle file upload logic
    const handleFileUpload = () => {
        if (selectedFiles.length > 0) {
            onUploadSuccess?.(selectedFiles);

            setSnackbarMessage('Files uploaded successfully!');
            setOpenSnackbar(true);
            setUploadedFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
            setSelectedFiles([]);
        } else {
            setSnackbarMessage('No files selected.');
            setOpenSnackbar(true);
        }
    };

    // Close Snackbar
    const handleCloseSnackbar = () => setOpenSnackbar(false);

    // Remove file from selected files list
    const handleRemoveFile = (fileName: string, source?: string) => {
        if (source === 'uploaded') {
            setUploadedFiles((prevFiles) => prevFiles.filter((file) => file.content.name !== fileName));
            return;
        }
        setSelectedFiles((prevFiles) => prevFiles.filter((file) => file.content.name !== fileName));
    };

    const handleDownloadFile = (file: UploadedFile, source?: string) => {
        const url = URL.createObjectURL(file.content);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', file.content.name);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        if (source !== 'uploaded') return;
        setUploadedFiles((prevFiles) => prevFiles.map((uploadedFile) => {
            if (uploadedFile.content.name === file.content.name) {
                uploadedFile.downloadCount += 1;
            }
            return uploadedFile;
        }));
    };

    const getFileIcon = (fileType: string) => {
        const type = fileType?.split('/')[0];
        console.log(type);
        switch (type) {
            case 'application':
            case 'pdf':
                return <PictureAsPdf />;
            case 'image':
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'webp':
            case 'svg':
                return <Image />;
            case 'doc':
            case 'docx':
            case 'docm':
                return <Assignment />;
            case 'mp3':
            case 'wav':
            case 'audio':
                return <AudioFile />;
            case 'mp4':
            case 'webm':
            case 'video':
                return <VideoFile />;
            case 'txt':
            case 'text':
                return <TextSnippet />;
            default:
                return <AttachFileIcon />;
        };
    };

    return (
        <>
            <Paper
                elevation={2}
                sx={{
                    padding: 6,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    border: '2px dashed #ccc',
                    borderRadius: 3,
                    cursor: 'pointer',
                    mt: 2,
                }}
                onClick={() => (document.querySelector('input[type="file"]') as HTMLInputElement)?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                    e.preventDefault();
                    const files = Array.from(e.dataTransfer.files);
                    const uploadedFiles = files.map((file) => ({
                        ...file,
                        uploadedBy: "user.id",
                        uploadedAt: new Date().toISOString(),
                        downloadCount: 0,
                        content: file,
                    }));
                    setSelectedFiles((prevFiles) => [...prevFiles, ...uploadedFiles]);
                }}
            >
                <UploadFile fontSize="large" />
                <Typography variant="h6" style={{ marginLeft: 16 }}>
                    Drag & Drop Files Here or Click to Upload
                </Typography>
                <input
                    type="file"
                    multiple
                    hidden
                    onChange={handleFileSelect}
                />
            </Paper>

            {selectedFiles.map((file) => (
                <Tooltip title={`Download ${file.content.name}`} key={file.content.name}>
                    <Card key={file.content.name} sx={{ padding: 2, marginTop: 2, display: 'flex', alignItems: 'center' }}>
                        {getFileIcon(file.content.type)}
                        <Typography onClick={() => handleDownloadFile(file)} variant="body1" style={{ marginLeft: 8 }}>{file.content.name}</Typography>
                        <Tooltip title="Remove file">
                            <IconButton onClick={() => handleRemoveFile(file.content.name)} style={{ marginLeft: 'auto' }}>
                                <Cancel sx={{
                                    '&:hover': {
                                        color: 'error.main'
                                    },
                                }} />
                            </IconButton>
                        </Tooltip>
                    </Card>
                </Tooltip>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="contained" color="primary" onClick={handleFileUpload} style={{ marginTop: 16 }}>
                    Upload Files
                </Button>
                <Button variant="outlined" color="primary" onClick={() => setSelectedFiles([])} style={{ marginTop: 16, marginLeft: 16 }}>
                    Clear Files
                </Button>
            </Box>

            {uploadedFiles.length !== 0 && <Table component={Paper} sx={{ marginTop: 2 }}>
                <TableHead>
                    <TableRow>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>File Name</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Size</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Uploader</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {uploadedFiles.map((file) => (
                        <TableRow key={file.content.name}>
                            <TableCell align="center" component="th" scope="row">
                                {file.content.name}
                            </TableCell>
                            <TableCell align="center">{(file.content.size / 1024).toFixed(2)} KB</TableCell>
                            <TableCell align="center">
                                <Tooltip onClick={() => console.log('open user page')} title={
                                    <Stack sx={{ padding: 1 }} direction="row" spacing={2} alignItems="center">
                                        <Avatar sx={{ width: 24, height: 24 }} alt="You" src="/path/to/avatar.jpg" />
                                        <Typography variant="body2">{file.uploadedBy}</Typography>
                                    </Stack>
                                }>
                                    <Typography variant="body2">{file.uploadedBy}</Typography>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                                <IconButton onClick={() => handleDownloadFile(file, 'uploaded')}>
                                    <Tooltip title="Download">
                                        <FileDownload />
                                    </Tooltip>
                                </IconButton>
                                {file.uploadedBy === 'user.id' && <IconButton onClick={() => handleRemoveFile(file.content.name, 'uploaded')}>
                                    <Tooltip title="Remove">
                                        <Cancel />
                                    </Tooltip>
                                </IconButton>}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>}


            {/* Snackbar for success/error message */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={snackbarMessage}
            />
        </>
    );
};

export default AttachmentUpload;
